import { useEffect, useState } from 'react'
import { adminAPI } from '../services/api'
import { apiErrorMessage } from '../utils/apiError'
import type { CapabilityReport, CapabilityStatus } from '../types/capabilities'

/**
 * Admin diagnostics: what the platform can do right now -- AI, market data,
 * Redis, scheduled tasks, Phase 2 model storage and the optional
 * integrations -- worst first, each with what to change (backend
 * services/capability_report). From configuration and recorded failures; no
 * live call, so "OK" means configured and not failing lately.
 */

const STATUS: Record<CapabilityStatus, { label: string; className: string }> = {
  down: { label: 'Down', className: 'bg-red-100 text-red-800' },
  degraded: { label: 'Degraded', className: 'bg-amber-100 text-amber-800' },
  not_configured: { label: 'Off', className: 'bg-gray-100 text-gray-600' },
  ok: { label: 'OK', className: 'bg-green-100 text-green-800' },
}

const SUMMARY: Record<CapabilityReport['status'], string> = {
  down: 'Something the platform needs is down.',
  degraded: 'Working, with something degraded.',
  ok: 'Everything it needs is working.',
}

const CapabilitiesPanel = () => {
  const [data, setData] = useState<CapabilityReport | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    adminAPI
      .capabilities()
      .then((d) => live && setData(d))
      .catch((e) => live && setError(apiErrorMessage(e, 'Could not load the capabilities')))
    return () => {
      live = false
    }
  }, [])

  return (
    <section className="rounded-lg border border-gray-200 bg-white" aria-label="Capabilities" data-testid="capabilities">
      <div className="border-b border-gray-200 p-4">
        <h2 className="text-lg font-semibold text-gray-900">What the platform can do</h2>
        {data && (
          <p className="mt-1 text-sm text-gray-600" data-testid="capabilities-summary">
            {SUMMARY[data.status]}
          </p>
        )}
      </div>
      {error && <p className="p-4 text-sm text-red-600">{error}</p>}
      {data && (
        <ul className="divide-y divide-gray-100">
          {data.capabilities.map((c) => {
            const status = STATUS[c.status]
            return (
              <li key={`${c.key}-${c.label}`} className="flex gap-3 px-4 py-3 text-sm" data-testid={`capability-${c.key}`}>
                <span className={`h-fit shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${status.className}`}>
                  {status.label}
                </span>
                <div className="min-w-0">
                  <div className="font-medium text-gray-800">{c.label}</div>
                  <div className="text-gray-600">{c.detail}</div>
                  {c.remedy && c.status !== 'ok' && <div className="mt-0.5 text-xs text-gray-500">Fix: {c.remedy}</div>}
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

export default CapabilitiesPanel
